<template>
  <div class="w-full h-full min-h-0 flex flex-col box-border">
    <div v-if="loading" class="p-3 text-sm text-gray-400 w-full items-center justify-center flex">{{ t('notebookPagesEditor.loadingPage') }}</div>

    <!-- Empty State -->
    <div v-if="!loading && pages.length === 0" 
      class="flex-1 flex flex-col text-gray-400 gap-4"
      :class="{'items-center justify-center ': canAddPage}">
      <div v-if="canAddPage">
        <div class="text-xl">{{ t('notebookPagesEditor.emptyTitle') }}</div>
        <button class="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors" 
            :class="{ 'cursor-not-allowed opacity-50': !canAddPage }"
            @click="addPageAfter(-1)" 
            :disabled="!canAddPage">
          <PlusIcon class="size-5" />
          <span>{{ t('notebookPagesEditor.addPage') }}</span>
        </button>
      </div>
      <div v-else>
        <div class="text-xl flex flex-row justify-center items-center gap-2">
          <ArrowTrendingDownIcon class="size-10 rotate-180"/>
          <span>{{ t('notebookPagesEditor.giveName') }}</span>
          </div>
      </div>
    </div>



    <div v-if="!loading && pages.length > 0" class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_200px] lg:grid-cols-[minmax(0,1fr)_300px] gap-3 flex-1 min-h-0 box-border">
      <div class="col-span-1 h-full min-h-0 box-border music-map flex flex-col gap-0">
        <NotebookPageEditor
          class="flex-1 min-h-0 !rounded-b-none !border-b-0 shadow-none"
          ref="pageEditorRef"
          v-if="pages[currentIndex] && !loading"
          :visible="true"
          :pageKey="pages[currentIndex].uid"
          :titleHtml="renderTitleHtml"
          :subtitleHtml="renderSubtitleHtml"
          :contentHtml="renderHtml"
          :mediaLink="getMediaLinkHtml()"
          :titlePlaceholder="titlePlaceholder"
          :subtitlePlaceholder="subtitlePlaceholder"
          :contentPlaceholder="pagePlaceholder"
          :isPublic="pages[currentIndex]?.isPublic || false"
          :userAdded="pages[currentIndex]?.userAdded || false"
          :tagsData="pages[currentIndex]?.tags || []"
          :semitone="pages[currentIndex]?.chord || 0"
          :autofocus="true"
          :currentVersionName="currentPageVersionName"
          @update:title="onTitleExternal"
          @update:subtitle="onSubtitleExternal"
          @update:artist="onArtistExternal"
          @update:tags="onTagsExternal"
          @update:isPublic="onIsPublicExternal"
          @update:content="onContentExternal"
          @update:mediaLink="onMediaLinkExternal"
          @transpose="onTransposeEvent"
          @update:semitone="onUpdateSemitoneEvent"
          @next="goNext"
          @focus-prev="focusPrev"
          @focus-next="focusNext"
          @update:data="onUpdateData"
          @clean:content="onCleanContent"
          @save:page="onSavePage"
          @showVersions="onShowVersions(currentIndex)"
          @error="onError"
        />
        
        <!-- Bottom Taskbar -->
        <div class="h-14 shrink-0 rounded-b-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20 flex items-center justify-between px-4 border-t border-t-gray-100 dark:border-t-gray-700/50">
          <div class="flex-1"></div>

          <!-- Center: Navigation -->
          <div class="flex items-center gap-2">
            <Tooltip :text="t('notebookPagesEditor.previousPage')" position="top">
              <button class="size-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" @click="goPrev" :disabled="currentIndex === 0">
                <ChevronLeftIcon class="size-5" />
              </button>
            </Tooltip>
            
            <span class="text-xs font-medium text-gray-500 min-w-[3rem] text-center">{{ currentIndex + 1 }} / {{ pages.length }}</span>
            
            <Tooltip :text="t('notebookPagesEditor.nextPage')" position="top">
              <button class="size-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300" @click="goNext" :disabled="currentIndex === pages.length - 1">
                <ChevronRightIcon class="size-5" />
              </button>
            </Tooltip>
          </div>

          <!-- Right: Save, Playlist & Delete -->
          <div class="flex items-center gap-2 flex-1 justify-end">
            <Tooltip :text="t('notebookPagesEditor.save')" position="top">
              <button class="px-4 py-2 flex items-center gap-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 font-medium text-sm transition-colors" 
                :disabled="pages.length === 0" @click="onSavePage">
                <CloudArrowUpIcon class="size-4" />
                {{ t('notebookPagesEditor.save') }}
              </button>
            </Tooltip>
            
            <Tooltip :text="t('notebookPagesEditor.goToPlaylist')" position="top">
              <button class="size-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors" 
                :disabled="pages.length === 0" @click="gotoScreen">
                <ComputerDesktopIcon class="size-4" />
              </button>
            </Tooltip>

            <div class="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1"></div>

            <Tooltip :text="t('notebookPagesEditor.deletePage')" position="top">
              <button class="size-8 flex items-center justify-center rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors" :disabled="pages.length <= 1" @click="removeCurrentPage">
                <TrashIcon class="size-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <NotebookSidebar
        v-if="!loading"
        :pages="pages"
        :page="currentIndex"
        :titles="titles"
        :previews="previews"
        @select="goTo"
        @reorder="onReorder"
        @moveToTop="onMoveToTop"
        @moveToBottom="onMoveToBottom"
        @createVersion="onCreateVersion"
        @showVersions="onShowVersions"
        @addSongToFavorite="onAddSongToFavorite"
        @addPage="addPageAfter(pages.length - 1)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, defineProps, defineEmits, computed } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useI18n } from 'vue-i18n'
import NotebookSidebar from './NotebookSidebar.vue'
import NotebookPageEditor from './NotebookPageEditor.vue'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, ComputerDesktopIcon, ArrowTrendingDownIcon } from '@heroicons/vue/24/outline'
import { CloudArrowUpIcon } from '@heroicons/vue/24/solid'
import toastStore from '@/store/toast.store';

const props = defineProps({
  name: { type: String, default: 'Notebook' },
  modelValue: { type: Array, default: () => [] }, // array of strings or objects { title, subtitle, content }
  autofocus: { type: Boolean, default: true },
  pagePlaceholder: { type: String, default: '' },
  titlePlaceholder: { type: String, default: '' },
  subtitlePlaceholder: { type: String, default: '' },
  pages: { type: Array, default: () => [] },
  headerClasses: { type: String, default: '' },
  loading: { type: Boolean, default: true },
  canAddPage: { type: Boolean, default: false }
})

const { t } = useI18n()

const emit = defineEmits(['update:modelValue', 'save:page', 'update:name', 'back', 'goto:playlist', 'clear:modified', 'delete:page', 'create:version', 'show:versions', 'addSongToFavorite'])

const pageEditorRef = ref(null)

const pages = ref(normalize(props.pages, []))
const currentIndex = ref(0)
const editorRef = ref(null) 
const renderHtml = ref('')
const renderTitleHtml = ref('')
const renderSubtitleHtml = ref('')
const loading = ref(true)
let isLocalTyping = false

// Computed property to check if there are unsaved changes
const hasUnsavedChanges = computed(() => {
  return pages.value.some(p => p.isModified === true || !p._id)
})

const hasIncompleteUnsavedPages = computed(() => {
  return pages.value.some(p => {
    if (p.isModified || !p._id) {
      const emptyContent = !p.content || !p.content.trim() || p.content.trim() === '<p></p>';
      const emptyTitle = !p.title || !p.title.trim();
      const emptyArtist = !p.metadata?._id && (!p.subtitle || !p.subtitle.trim());
      return emptyContent || emptyTitle || emptyArtist;
    }
    return false;
  })
})


const getMediaLinkHtml = () => {
  return pages.value[currentIndex.value]?.mediaLink || ''
}

// Computed property to get the current page's version name
const currentPageVersionName = computed(() => {
  const currentPage = pages.value[currentIndex.value]
  return currentPage?.appliedVersionName || null
})


function normalize(input, existingPages = []) {
  return (input && Array.isArray(input) ? input : []).map((item, idx) => {
    if (typeof item === 'string') {
      // Try to find existing page by index or create new
      const existing = existingPages[idx]
      return {
        uid: existing?.uid || `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
        _id: existing?._id || item._id || null,
        title: existing?.title || '',
        subtitle: existing?.subtitle || '',
        isPublic: existing?.isPublic || false,
        userAdded: existing?.userAdded || false,
        content: item,
        mediaLink: existing?.mediaLink || '',
        tags: existing?.tags || [],
        originalContent: item,
        metadata: existing?.metadata || null,
        chord: existing?.chord || 0,
        isModified: existing?.isModified || false
      }
    }
    // Try to find existing page by _id or uid, otherwise by index
    const existingById = item?._id ? existingPages.find(p => p._id === item._id) : null
    const existingByIndex = existingPages[idx]
    const existing = existingById || existingByIndex
    
    return {
      uid: existing?.uid || item?.uid || `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      _id: item?._id || existing?._id || null,
      title: item?.title || '',
      subtitle: item?.subtitle || '',
      isPublic: item?.isPublic || existing?.isPublic || false,
      userAdded: item?.userAdded || existing?.userAdded || false,
      artist: item?.artist || existing?.artist || null,
      content: item?.content || '',
      mediaLink: item?.mediaLink || '',
      tags: item?.tags || existing?.tags || [],
      originalContent: item?.originalContent !== undefined ? item.originalContent : (!item?.originalContent ? item.content : null),
      metadata: item?.metadata || existing?.metadata || null,
      appliedVersionId: item?.appliedVersionId || existing?.appliedVersionId || null,
      appliedVersionName: item?.appliedVersionName || existing?.appliedVersionName || null,
      chord: item?.chord !== undefined ? item.chord : (existing?.chord || 0),
      isModified: item?.isModified !== undefined ? item.isModified : (existing?.isModified || false)
    }
  })
}

function ensureAtLeastOnePage() {
  // Removed to support empty state
}

function syncOut() {
  emit('update:modelValue', pages.value.map(p => ({ 
    uid: p.uid,
    _id: p._id,
    title: p.title || '', 
    subtitle: p.subtitle || '', 
    isPublic: p.isPublic || false,
    content: p.content || '', 
    mediaLink: p.mediaLink || '',
    artist: p.artist || null,
    tags: p.tags || [],
    metadata: p.metadata || null, 
    chord: p.chord || 0,
    isModified: p.isModified || false,
    userAdded: p.userAdded || false,
    originalContent: p.originalContent || null,
    appliedVersionId: p.appliedVersionId || p.metadata?.songVersionId || null,
    appliedVersionName: p.appliedVersionName || null
  })));
}

function onTransposeEvent(event) {
  emit('transpose', { pageIndex: currentIndex.value, page: pages.value[currentIndex.value], event });
}

function onUpdateSemitoneEvent(semitone) {
  if (pages.value[currentIndex.value]) {
    pages.value[currentIndex.value].chord = semitone;
    pages.value[currentIndex.value].isModified = true;
    syncOut();
  }
}


function focusEditor() {
  nextTick(() => {
    if (editorRef.value) {
      if (document.activeElement && document.activeElement !== editorRef.value && document.activeElement.blur) {
        document.activeElement.blur()
      }
      editorRef.value.focus()
    }
  })
}

function cleanProperties() {
  renderSubtitleHtml.value = '';
  renderTitleHtml.value = '';
  renderHtml.value = '';
}
function goTo(index) {
  if (pageEditorRef.value) {
    pageEditorRef.value.reset()
  }
  if (index < 0 || index >= pages.value.length) return
  cleanProperties()
  currentIndex.value = index
  nextTick(() => {
    renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
    renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
    renderHtml.value = pages.value[currentIndex.value]?.content || ''
    focusEditor()
  })
}

function goNext() {
  if (pageEditorRef.value) {
    pageEditorRef.value.reset()
  }
  if (currentIndex.value === pages.value.length - 1) {
    pages.value.push({ uid: `${Date.now()}-${Math.random()}`, title: '', subtitle: '', content: '', metadata: null })
    syncOut()
    currentIndex.value = pages.value.length - 1
    cleanProperties()
    nextTick(() => {
      focusEditor()
    })
  } else {
    currentIndex.value = currentIndex.value + 1
    cleanProperties()
    nextTick(() => {
      renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
      renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
      renderHtml.value = pages.value[currentIndex.value]?.content || ''
      focusEditor()
    })
  }
}

function goPrev() {
  if (pageEditorRef.value) {
    pageEditorRef.value.reset()
  }
  if (currentIndex.value > 0) {
    currentIndex.value = currentIndex.value - 1
    cleanProperties()
    nextTick(() => {
      renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
      renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
      renderHtml.value = pages.value[currentIndex.value]?.content || ''
      focusEditor()
    })
  }
}

function addPageAfter(index) {
  if (pageEditorRef.value) {
    pageEditorRef.value.reset()
  }
  const insertIndex = Math.min(index + 1, pages.value.length)
  pages.value.splice(insertIndex, 0, { uid: `${Date.now()}-${Math.random()}`, title: '', subtitle: '', content: '', metadata: null })
  syncOut()
  currentIndex.value = insertIndex
  cleanProperties()
  nextTick(() => focusEditor())
}

function onMoveToTop(idx) {
  if (idx === 0) return;
  onReorder({ oldIndex: idx, newIndex: 0 });
}

function onMoveToBottom(idx) {
  if (idx === pages.value.length - 1) return;
  onReorder({ oldIndex: idx, newIndex: pages.value.length - 1 });
}

function onAddSongToFavorite(idx) {
  const page = pages.value[idx]
  if (!page) return
  emit('addSongToFavorite', {
    page,
    index: idx,
    songId: page._id || page.metadata?._id,
    title: page.title,
    appliedVersionId: page.appliedVersionId || page.metadata?.songVersionId || null
  })
}

function onCreateVersion(idx) {
  const page = pages.value[idx]
  if (!page) return
  
  // Emit the create:version event with the page data
  emit('create:version', {
    page,
    index: idx,
    songId: page._id || page.metadata?._id,
    title: page.title,
    content: page.content,
    artist: page.artist,
    mediaLink: page.mediaLink
  })
}

function onShowVersions(idx) {
  const page = pages.value[idx]
  if (!page) {
    return
  }
  
  const songId = page._id || page.metadata?._id
  
  emit('show:versions', {
    page,
    index: idx,
    songId: songId,
    title: page.title,
    content: page.content,
    appliedVersionId: page.appliedVersionId || page.metadata?.songVersionId || null
  })
}

function removeCurrentPage() {
  if (pages.value.length === 0) return;
  
  let removedPage = null;

  if (pages.value.length <= 1) {
    // If it's the last page, we remove it to empty state
    removedPage = pages.value.splice(0, 1)[0];
    currentIndex.value = 0;
  } else {
    removedPage = pages.value.splice(currentIndex.value, 1)[0];
    if (currentIndex.value >= pages.value.length) {
      currentIndex.value = pages.value.length - 1
    }
    if (pageEditorRef.value) {
      pageEditorRef.value.reset()
    }
  }

  if (removedPage && removedPage._id) {
    emit('delete:page', removedPage._id);
  }

  syncOut()
  if (pages.value.length > 0) {
    renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
    renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
    renderHtml.value = pages.value[currentIndex.value]?.content || ''
    focusEditor()
  }
  // Otherwise, clear properties
  else {
    cleanProperties()
  }
}



function onContentExternal(html) {
  if (!pages.value[currentIndex.value]) return;
  if (!pages.value[currentIndex.value].isModified) {
    const divPage = document.createElement('div');
    divPage.innerHTML = pages.value[currentIndex.value].content || '';
    const divHtml = document.createElement('div');
    divHtml.innerHTML = html || '';
    
    const cleanHtml = (container) => {
      container.querySelectorAll('.chord-container').forEach(c => {
        c.removeAttribute('data-spacer');
        c.style.marginRight = '';
      });
      let out = container.innerHTML.trim();
      if (out === '<p></p>') out = '';
      return out;
    };

    pages.value[currentIndex.value].isModified = cleanHtml(divPage) !== cleanHtml(divHtml);
  }
  pages.value[currentIndex.value].content = html
  
  isLocalTyping = true
  syncOut()
}

function onTitleExternal(title) {
  if (!pages.value[currentIndex.value]) return
  if (!pages.value[currentIndex.value].isModified) {
    pages.value[currentIndex.value].isModified = pages.value[currentIndex.value].title !== title;
  }
  pages.value[currentIndex.value].title = title
  
  isLocalTyping = true
  syncOut()
}

function onSubtitleExternal(subtitle) {
  if (!pages.value[currentIndex.value]) return
  if (!pages.value[currentIndex.value].isModified) {
    pages.value[currentIndex.value].isModified = pages.value[currentIndex.value].subtitle !== subtitle;
  }
  pages.value[currentIndex.value].subtitle = subtitle
  isLocalTyping = true
  syncOut()
}

const onTagsExternal = (newTags) => {
  if (pages.value[currentIndex.value]) {
    pages.value[currentIndex.value].tags = newTags
    pages.value[currentIndex.value].isModified = true
    isLocalTyping = true
    syncOut()
  }
}

function onIsPublicExternal(isPublic) {
  if (!pages.value[currentIndex.value]) return
  if (!pages.value[currentIndex.value].isModified) {
    pages.value[currentIndex.value].isModified = pages.value[currentIndex.value].isPublic !== isPublic;
  }
  pages.value[currentIndex.value].isPublic = isPublic
  isLocalTyping = true
  syncOut()
}

function onArtistExternal(artist) {
  if (!pages.value[currentIndex.value]) return
  pages.value[currentIndex.value].artist = artist
  if (artist?.name) {
    pages.value[currentIndex.value].subtitle = artist.name
  }
  if (!pages.value[currentIndex.value].isModified) {
    pages.value[currentIndex.value].isModified = true
  }
  isLocalTyping = true
  syncOut()
}

function onMediaLinkExternal(mediaLink) {
  if (!pages.value[currentIndex.value]) return
  if (!pages.value[currentIndex.value].isModified) {
    pages.value[currentIndex.value].isModified = pages.value[currentIndex.value].mediaLink !== mediaLink;
  }
  pages.value[currentIndex.value].mediaLink = mediaLink;
  isLocalTyping = true
  syncOut()
}

function shortPreview(text) {
  const t = htmlToText(text || '').trim()
  if (!t) return 'Empty'
  return t.length > 18 ? t.slice(0, 18) + '…' : t
}

function htmlToText(html) {
  try {
    const el = document.createElement('div')
    el.innerHTML = html || ''
    return (el.textContent || el.innerText || '')
  } catch (e) {
    return (html || '').replace(/<[^>]*>/g, '')
  }
}

const titles = computed(() => pages.value.map((p, idx) => htmlToText(p?.title || '').trim() || `Page ${idx + 1}`))
const previews = computed(() => pages.value.map((p) => {
  const sub = htmlToText(p?.subtitle || '').trim()
  return sub ? shortPreview(sub) : shortPreview(p?.content || '')
}))

watch(() => props.modelValue, (val) => {
  const incoming = (val && Array.isArray(val)) ? val : []
  const incomingData = incoming.map(p => typeof p === 'string' ? { content: p } : {
    content: p.content || '',
    title: p.title || '',
    subtitle: p.subtitle || '',
    mediaLink: p.mediaLink || '',
    tags: p.tags || [],
    isModified: p.isModified || false
  })
  const current = pages.value.map(p => ({
    content: p.content || '',
    title: p.title || '',
    subtitle: p.subtitle || '',
    mediaLink: p.mediaLink || '',
    tags: p.tags || [],
    isModified: p.isModified || false
  }))
  const currentPageContentChanged = current[currentIndex.value]?.content !== incomingData[currentIndex.value]?.content
  const currentPageTitleChanged = current[currentIndex.value]?.title !== incomingData[currentIndex.value]?.title
  const currentPageSubtitleChanged = current[currentIndex.value]?.subtitle !== incomingData[currentIndex.value]?.subtitle

  const isSame = JSON.stringify(current) === JSON.stringify(incomingData)
  
  if (!isSame) {
    pages.value = normalize(incoming, pages.value)
    ensureAtLeastOnePage()
    if (currentIndex.value >= pages.value.length) currentIndex.value = pages.value.length - 1
    
    // Only update innerHTML binding when the change is external
    nextTick(() => {
      if (!isLocalTyping && pages.value.length > 0) {
        if (currentPageTitleChanged) renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
        if (currentPageSubtitleChanged) renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
        if (currentPageContentChanged) renderHtml.value = pages.value[currentIndex.value]?.content || ''
      }
      isLocalTyping = false
    })
  } else {
    // local cycle ends
    isLocalTyping = false
  }
}, { immediate: true, deep: true })

onMounted(() => {
  // ensureAtLeastOnePage()
  if (props.autofocus && pages.value.length > 0) focusEditor()
  if (pages.value.length > 0) {
    renderTitleHtml.value = pages.value[currentIndex.value]?.title || ''
    renderSubtitleHtml.value = pages.value[currentIndex.value]?.subtitle || ''
    renderHtml.value = pages.value[currentIndex.value]?.content || ''
  }

  // Sortable for page chips (reorder)
  // handled within NotebookSidebar
})

function onReorder({ oldIndex, newIndex }) {
  const moved = pages.value.splice(oldIndex, 1)[0]
  pages.value.splice(newIndex, 0, moved)
  if (currentIndex.value === oldIndex) {
    currentIndex.value = newIndex
  } else if (oldIndex < currentIndex.value && newIndex >= currentIndex.value) {
    currentIndex.value = currentIndex.value - 1
  } else if (oldIndex > currentIndex.value && newIndex <= currentIndex.value) {
    currentIndex.value = currentIndex.value + 1
  }
  pages.value.forEach((p, idx) => {
    p.order = idx + 1
  });
  syncOut()
  onSavePage()
}


function onCleanContent(target) {
  if (!pages.value[currentIndex.value] || !target) return

  callCleanHtmlWorker(target.innerHTML || '')
}

function callCleanHtmlWorker(html) {
  const worker = new Worker(new URL('@/service/cleanHtmlWorker.js?worker', import.meta.url), {
    type: 'module'
  });

  worker.postMessage({ html });

  worker.addEventListener('message', (e) => {
      if (e.data.success) {
        renderHtml.value = e.data.result;
        onContentExternal(e.data.result);
      } else {
          console.error('Error:', e.data.error);
      }
  });
}
function onSavePage() {
  console.log("onSavePage")

  for (let i = 0; i < pages.value.length; i++) {
    const p = pages.value[i];
    const pageNum = i + 1;
    const pageTitle = p.title || `Page ${pageNum}`;
    
    if (!p.title || !p.title.trim()) {
      toastStore.dispatch('addMessage', {
        message: `Page ${pageNum} is missing a title.`,
        icon: 'error',
        ttl: 5000
      });
      goTo(i);
      return false;
    }
    
    // Subtitle (artist) is required if it's a new song
    if (!p.metadata?._id && (!p.subtitle || !p.subtitle.trim())) {
      toastStore.dispatch('addMessage', {
        message: `Page ${pageNum} ("${pageTitle}") is missing an artist (subtitle).`,
        icon: 'error',
        ttl: 5000
      });
      goTo(i);
      return false;
    }
    
    if (!p.content || !p.content.trim()) {
      toastStore.dispatch('addMessage', {
        message: `Page ${pageNum} ("${pageTitle}") is missing content (lyrics).`,
        icon: 'error',
        ttl: 5000
      });
      goTo(i);
      return false;
    }
  }

  if (pages.value.length > 0)
    emit('save:page', pages.value, pages.value[currentIndex.value])
  else
    emit('save:page', pages.value, null)
  
  return true;
  // Clear isModified flags after save is triggered
  // The actual clearing will happen when save is successful and pages are updated from server
}

// Function to clear isModified flags (called after successful save)
function clearModifiedFlags() {
  pages.value.forEach(page => {
    page.isModified = false
  })
  syncOut()
}

watch( () => props.pages, (newVal) => {
  if (!newVal || !Array.isArray(newVal)) return
  
  const normalized = normalize(newVal, pages.value)
  
  // Check if arrays are different by comparing length and IDs
  const currentIds = pages.value.map(p => p._id || p.uid || '').join(',')
  const newIds = normalized.map(p => p._id || p.uid || '').join(',')
  const lengthChanged = pages.value.length !== normalized.length
  const idsChanged = currentIds !== newIds
  
  // Always update if length changed (items added/removed) or IDs changed
  if (lengthChanged || idsChanged) {
    pages.value = normalized
    ensureAtLeastOnePage()
    
    // Only adjust index if current page no longer exists
    if (currentIndex.value >= pages.value.length) {
      currentIndex.value = Math.max(0, pages.value.length - 1)
    }
    
    // Update render values for current page
    nextTick(() => {
      if (!isLocalTyping && pages.value.length > 0) {
        const title = pages.value[currentIndex.value]?.title || ''
        const subtitle = pages.value[currentIndex.value]?.subtitle || ''
        const content = pages.value[currentIndex.value]?.content || ''
        
        if (renderTitleHtml.value !== title) renderTitleHtml.value = title
        if (renderSubtitleHtml.value !== subtitle) renderSubtitleHtml.value = subtitle
        if (renderHtml.value !== content) renderHtml.value = content
      }
    })
  } else {
    if (!isLocalTyping) {
      let anyContentChanged = false
      for (let i = 0; i < pages.value.length; i++) {
        if (pages.value[i].title !== normalized[i].title ||
            pages.value[i].subtitle !== normalized[i].subtitle ||
            pages.value[i].content !== normalized[i].content ||
            pages.value[i].appliedVersionId !== normalized[i].appliedVersionId ||
            pages.value[i].appliedVersionName !== normalized[i].appliedVersionName ||
            pages.value[i].mediaLink !== normalized[i].mediaLink ||
            JSON.stringify(pages.value[i].artist) !== JSON.stringify(normalized[i].artist)) {
          pages.value[i].title = normalized[i].title
          pages.value[i].subtitle = normalized[i].subtitle
          pages.value[i].content = normalized[i].content
          pages.value[i].appliedVersionId = normalized[i].appliedVersionId
          pages.value[i].appliedVersionName = normalized[i].appliedVersionName
          pages.value[i].mediaLink = normalized[i].mediaLink
          pages.value[i].artist = normalized[i].artist
          anyContentChanged = true
        }
      }
      
      if (anyContentChanged) {
        nextTick(() => {
          const title = pages.value[currentIndex.value]?.title || ''
          const subtitle = pages.value[currentIndex.value]?.subtitle || ''
          const content = pages.value[currentIndex.value]?.content || ''
          
          if (renderTitleHtml.value !== title) renderTitleHtml.value = title
          if (renderSubtitleHtml.value !== subtitle) renderSubtitleHtml.value = subtitle
          if (renderHtml.value !== content) renderHtml.value = content
        })
      }
    }
  }
  isLocalTyping = false
}, { deep: true, immediate: true })

// Vue Router navigation guard to intercept route changes
onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    if (hasIncompleteUnsavedPages.value) {
      if (confirm(t('notebookPagesEditor.leaveUnsavedConfirmation') || 'You have songs with missing information (title, artist, or content). Do you want to leave without saving these songs?')) {
        next()
      } else {
        next(false)
      }
      return;
    }
    
    // All pages are complete, so auto-save and leave
    onSavePage();
    next();
  } else {
    next()
  }
})

function onUpdateData(data) {
  if (!pages.value[currentIndex.value]) return
  pages.value[currentIndex.value].title = data.title
  pages.value[currentIndex.value].subtitle = data.subtitle
  pages.value[currentIndex.value].mediaLink = data.mediaLink
  pages.value[currentIndex.value].content = data.content
  pages.value[currentIndex.value].isModified  = data.isModified;
  isLocalTyping = true
  syncOut()
}


function gotoScreen() {
  if (pages.value.length > 0)
    emit('goto:screen', pages.value[0]._id)
}

function onError(error) {
  toastStore.dispatch('addMessage', {
    message: error,
    icon: 'error',
    ttl: 3000
  });
}

watch(() => props.loading, (newVal) => {
  loading.value = newVal
})

// Watch for pages updates from server (after successful save)
// Clear isModified flags when pages are updated from server
watch(() => props.pages, (newPages, oldPages) => {
  if (!newPages || !Array.isArray(newPages)) return
  
  // Check if we had modified pages before and now they're not modified
  // This indicates a successful save from server
  const hadModified = oldPages && Array.isArray(oldPages) 
    ? oldPages.some(p => p.isModified === true)
    : pages.value.some(p => p.isModified === true)
  const nowModified = newPages.some(p => p.isModified === true)
  
  // If we had modified pages and now they're not modified, clear the flags
  if (hadModified && !nowModified) {
    // Clear modified flags after successful save
    clearModifiedFlags()
  }
}, { deep: true })
</script>

<style scoped>
[contenteditable][data-placeholder]:empty:before {
  content: attr(data-placeholder);
  color: rgb(156 163 175); /* gray-400 */
}
</style>

